'use client';

import type { JSX } from 'react';

import { ParentSize } from '@visx/responsive';

import GraphSectionWrapper from 'components/Graph/components/GraphSectionWrapper';
import ParentSizeWrapper from 'components/Graph/components/ParentSizeWrapper';
import type { KpowHistoryPoint } from 'react_pages/AmericanPowerIndex/api/kpowApi';

import type { KpowAnnotation } from '../kpowAnnotations';
import type { KpowPeriod } from '../kpowFormat';
import IndexGraphContent from './IndexGraphContent';

export type IndexGraphPresentation = 'kpow' | 'midterms';

interface IndexGraphProps {
    points: KpowHistoryPoint[];
    annotations?: KpowAnnotation[];
    /** Selected time range; drives x-axis tick granularity (hours / days / months). */
    period?: KpowPeriod;
    /** Fires with the hovered point during scrub, or null when the cursor leaves. */
    onHoverPoint?: (point: KpowHistoryPoint | null) => void;
    /** Opt-in visual treatment; omitted keeps the existing KPOW chart unchanged. */
    presentation?: IndexGraphPresentation;
}

/** Responsive wrapper around IndexGraphContent: measures its container and renders the chart. */
const IndexGraph = ({
    points,
    annotations,
    period,
    onHoverPoint,
    presentation = 'kpow',
}: IndexGraphProps): JSX.Element => (
    <GraphSectionWrapper transparent>
        <ParentSizeWrapper isCompact={false}>
            {/* debounceTime={0} so the chart re-measures immediately on resize. The default 300ms
                debounce leaves the SVG at its previous (e.g. taller phone) height for a beat when the
                container's height changes across the sm: breakpoint, and overflow:visible spills that
                stale render down into the footer. */}
            <ParentSize debounceTime={0} style={{ display: 'flex', width: '100%', height: '100%' }}>
                {(parent) => (
                    <IndexGraphContent
                        width={parent.width}
                        height={parent.height}
                        points={points}
                        annotations={annotations}
                        period={period}
                        onHoverPoint={onHoverPoint}
                        presentation={presentation}
                    />
                )}
            </ParentSize>
        </ParentSizeWrapper>
    </GraphSectionWrapper>
);

export default IndexGraph;
